/**
 * Published formulas API: formulas published outside the workspace (shared and read-only), the perfume
 * each smells like, and the copy of one into the workspace's formulas, under /api/published-formulas/
 * (backend/published_formulas/api.py). Amounts are decimal strings as published, grams as weighed.
 * Query keys start with "published-formulas".
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/api/client";
import type { AssetCopy, DecimalString, IsoDateTime, MinorUnits, Uuid } from "@/api/types";
import type { StockCounts, StockState } from "@/lib/stock";

export type FormulaAccess = "free" | "paid";
export type NotesTier = "top" | "heart" | "base" | "all";

export interface PerfumeBrief {
  id: Uuid;
  name: string;
  brand: string;
  year: number | null;
  /** The stored bottle image, "" when there is none. */
  image_url: string;
}

export interface PerfumeDetail extends PerfumeBrief {
  concentration: string;
  perfumers: string[];
  /** The notes pyramid; "all" when the source lists notes without one. */
  notes: Partial<Record<NotesTier, string[]>>;
  description: string;
  /** Scento's classification, when Scento describes the perfume. */
  profile: {
    family?: string;
    secondary_family?: string;
    character?: string[];
    seasons?: string[];
    times_of_day?: string[];
    popularity_rank?: number;
  };
  source: string;
  retrieved_at: IsoDateTime | null;
}

export interface PerfumeLink {
  site: "Parfumo" | "Fragrantica" | "Basenotes" | "Scento";
  url: string;
  /** "page" links the perfume itself, "search" a search for it. */
  kind: "page" | "search";
}

export interface PublishedFormulaSummary {
  id: Uuid;
  name: string;
  source: { id: string; name: string };
  style: string;
  description: string;
  /** As published: "Tom Ford F*** Fabulous (2017)". */
  smells_like: string;
  perfume: PerfumeBrief | null;
  /** "gcms_analysis": the perfume's measured composition, in percent. */
  kind: "formula" | "gcms_analysis";
  /** A GC-MS analysis known only from the shares a store's product pages give. */
  partial: boolean;
  access: FormulaAccess;
  price_minor: MinorUnits | null;
  currency: string;
  url: string;
  document_url: string;
  published_at: IsoDateTime | null;
}

export interface PublishedFormulaRow extends PublishedFormulaSummary {
  line_count: number;
  /** Lines matched to a material of the library. */
  matched_count: number;
  /** The matched lines whose material the workspace has in stock, on order, or neither. */
  stock_counts: StockCounts;
}

export interface PublishedFormulaList {
  count: number;
  sources: { id: string; name: string; count: number }[];
  items: PublishedFormulaRow[];
}

export interface PublishedLine {
  id: Uuid;
  position: number;
  text: string;
  name: string;
  amount: DecimalString;
  /** Of the formula as published. */
  percent: DecimalString | null;
  /** "0.1": weighed as a 10% dilution. */
  active_fraction: DecimalString;
  carrier_text: string;
  cas: string;
  material: { id: Uuid; name: string; stock: StockState } | null;
  store_product: { title: string; url: string } | null;
  match_method: string;
}

export interface PublishedFormulaDetail extends PublishedFormulaSummary {
  /** Our copy of the formula sheet (or of Inscentify's analysis), when one was saved. */
  document_copy: AssetCopy | null;
  code: string;
  notes: string;
  unit: "g" | "parts" | "pct";
  stated_total: DecimalString | null;
  total: DecimalString | null;
  parse_warnings: string[];
  retrieved_at: IsoDateTime | null;
  perfume: PerfumeDetail | null;
  /** What the perfume lookup found, or why it found nothing. */
  perfume_note: string;
  links: PerfumeLink[];
  lines: PublishedLine[];
}

export interface CopiedFormula {
  formula_id: Uuid;
  version_id: Uuid;
  copied: number;
  /** Lines left out: no material of the library matched them. */
  skipped: string[];
}

export interface PublishedFormulaParams {
  q?: string;
  source?: string;
  material?: Uuid;
  access?: FormulaAccess;
  limit?: number;
  offset?: number;
}

export const publishedFormulaKeys = {
  all: ["published-formulas"] as const,
  list: (params: PublishedFormulaParams) => ["published-formulas", "list", params] as const,
  formula: (id: Uuid) => ["published-formulas", "formula", id] as const,
};

export function usePublishedFormulas(params: PublishedFormulaParams, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: publishedFormulaKeys.list(params),
    queryFn: ({ signal }) => apiGet<PublishedFormulaList>("/published-formulas/", { params: { ...params }, signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function usePublishedFormula(id: Uuid | undefined) {
  return useQuery({
    queryKey: publishedFormulaKeys.formula(id ?? ""),
    queryFn: ({ signal }) => apiGet<PublishedFormulaDetail>(`/published-formulas/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

/** Copy a published formula into the workspace's formulas (a new formula with a draft version). */
export function useCopyPublishedFormula(id: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<CopiedFormula>(`/published-formulas/${id}/copy`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["formulas"] }),
  });
}
